import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

const FALLBACK_MENU = [
  { titel: "Forside", url: "/", sortering: 1, parent_slug: null },
  { titel: "Kategorier", url: "/kategorier", sortering: 2, parent_slug: null },
  { titel: "Billeder/film og lyd", url: "/billeder-film-og-lyd", sortering: 3, parent_slug: "kategorier" },
  { titel: "Børnenes univers", url: "/bornenes-univers", sortering: 4, parent_slug: "kategorier" },
  { titel: "Design", url: "/design", sortering: 5, parent_slug: "kategorier" },
  { titel: "Elektonik", url: "/elektonik", sortering: 6, parent_slug: "kategorier" },
  { titel: "Lamper", url: "/lamper", sortering: 7, parent_slug: "kategorier" },
  { titel: "Læsestof", url: "/laesestof", sortering: 8, parent_slug: "kategorier" },
  { titel: "Malerier", url: "/malerier", sortering: 9, parent_slug: "kategorier" },
  { titel: "Møbler", url: "/mobler", sortering: 10, parent_slug: "kategorier" },
  { titel: "Porcelæn og keramik", url: "/porcelan-og-keramik", sortering: 11, parent_slug: "kategorier" },
  { titel: "Småting til hjemmet", url: "/smating-til-hjemmet", sortering: 12, parent_slug: "kategorier" },
  { titel: "Butikkens personale", url: "/personale", sortering: 20, parent_slug: null },
  { titel: "Butikkens historie", url: "/historie", sortering: 21, parent_slug: null },
  { titel: "Kontakt", url: "/kontakt", sortering: 22, parent_slug: null }
];

export default function Header() {
  const [menuItems, setMenuItems] = useState(FALLBACK_MENU);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    fetch("https://api.dev.auxo.dk/public/content/menu_items")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = data.sort((a, b) => (a.sortering || 0) - (b.sortering || 0));
          setMenuItems(sorted);
        }
      })
      .catch((err) => console.error("Error fetching menu items:", err));
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  // Separate main items and children
  const mainItems = menuItems.filter((item) => !item.parent_slug);
  const getChildren = (parentUrl) => {
    // Parent slug is usually the url segment like 'loppeting'
    const parentSlug = parentUrl.replace("/", "");
    return menuItems.filter((item) => item.parent_slug === parentSlug);
  };

  const isActive = (url) => {
    if (url === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(url);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#f26a36] text-brand-blue shadow-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        {/* Logo / Brand */}
        <Link to="/" className="flex items-center gap-3 group">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue text-brand-orange transition-transform group-hover:scale-105">
            <i className="fa-solid fa-store text-xl" />
          </div>
          <div>
            <span className="block font-display text-2xl font-bold tracking-wide text-brand-blue">
              Havneloppen
            </span>
            <span className="block text-xs uppercase tracking-widest text-brand-blue/80 font-semibold">
              Komission og Genbrug
            </span>
          </div>
        </Link>

        {/* Desktop Navigation (Visible >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-8">
          {mainItems.map((item) => {
            const children = getChildren(item.url);
            const hasChildren = children.length > 0;

            if (hasChildren) {
              return (
                <div key={item.url} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    className={`flex items-center gap-2 font-medium text-sm uppercase tracking-wider transition-colors hover:text-brand-blue-hover py-2 ${
                      isActive(item.url) ? "text-brand-blue font-bold" : "text-brand-blue/80"
                    }`}
                  >
                    {item.titel}
                    <i className={`fa-solid fa-chevron-down text-xs transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div
                      onMouseLeave={() => setIsDropdownOpen(false)}
                      className="absolute left-0 mt-1 w-64 max-h-[80vh] overflow-y-auto rounded-lg bg-brand-orange border border-brand-orange-hover p-2 shadow-xl animate-in fade-in slide-in-from-top-2 duration-150 z-50"
                    >
                      {children.map((child) => (
                        <Link
                          key={child.url}
                          to={child.url}
                          className={`block rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-brand-orange-hover hover:text-brand-blue ${
                            location.pathname === child.url ? "text-brand-blue font-bold bg-brand-orange-hover/30" : "text-brand-blue/80"
                          }`}
                        >
                          {child.titel}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.url}
                to={item.url}
                className={`font-medium text-sm uppercase tracking-wider transition-colors hover:text-brand-blue-hover py-2 ${
                  isActive(item.url) ? "text-brand-blue font-bold border-b-2 border-brand-blue" : "text-brand-blue/80"
                }`}
              >
                {item.titel}
              </Link>
            );
          })}
        </nav>

        {/* Burgermenu Button (Visible < 1200px) */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex h-10 w-10 items-center justify-center rounded-md border border-brand-blue/20 text-brand-blue hover:bg-brand-orange-hover min-[1200px]:hidden"
          aria-label="Toggle menu"
        >
          <i className={`fa-solid ${isMobileMenuOpen ? "fa-xmark" : "fa-bars"} text-xl`} />
        </button>
      </div>

      {/* Mobile Navigation Menu (Visible < 1200px) */}
      {isMobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-brand-orange-hover bg-brand-orange px-6 py-4 shadow-inner">
          <nav className="flex flex-col gap-4">
            {mainItems.map((item) => {
              const children = getChildren(item.url);
              const hasChildren = children.length > 0;

              if (hasChildren) {
                return (
                  <div key={item.url} className="flex flex-col gap-2">
                    <div className="font-semibold text-xs uppercase tracking-widest text-brand-blue mt-2">
                      {item.titel}
                    </div>
                    <div className="pl-4 flex flex-col gap-3 border-l border-brand-blue/20">
                      {children.map((child) => (
                        <Link
                          key={child.url}
                          to={child.url}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`text-sm font-medium transition-colors hover:text-brand-blue-hover ${
                            location.pathname === child.url ? "text-brand-blue font-bold" : "text-brand-blue/80"
                          }`}
                        >
                          {child.titel}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={item.url}
                  to={item.url}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`text-base font-medium uppercase tracking-wider transition-colors hover:text-brand-blue-hover py-1 ${
                    isActive(item.url) ? "text-brand-blue font-bold" : "text-brand-blue/80"
                  }`}
                >
                  {item.titel}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}
